import * as WebAuthnJSON from '@github/webauthn-json'
import * as sha256Browser from "@aws-crypto/sha256-browser";

const DEVICE_TRUST_SSO_TIMEOUT = 2500;

let deviceTrustSsoTimeout;

function toHex(uint8Array) {
  // Array.from is not supported in IE
  return [].slice.call(uint8Array)
    .map((x) => {
      let xStr = x.toString(16);
      // padStart is not supported in IE, manually padding the string
      while (xStr.length < 2) {
        xStr = "0" + xStr;
      }
      return xStr;
    })
    .join('');
}

async function computeHash(text) {
  const hash = new sha256Browser.Sha256();
  hash.update(text);
  const result = await hash.digest();
  return toHex(result);
}

function subscribeToFayeChannel(channel, callback) {
  const fayeServerUrl = $("#faye-url").attr('data-fayeServerUrl');
  const faye = new Faye.Client(fayeServerUrl);
  faye.subscribe(`/messages/${channel}`, callback);
}

function bindNavigationEvents() {
  const federationButton = $("#federation-button");
  const qrButton = $("#qrButton");
  const usernameButton = $("#usernameButton");
  const passkeyButton = $("#passkey-button");
  const deviceTrustSsoButton = $('#device-trust-sso-button')

  if (federationButton) {
    federationButton.on("click", () => {
      ["qrField", "usernameField", "federation-button"].forEach(hideElement);
      ["qrButton", "usernameButton", "federation"].forEach(showElement);
    });
  }

  if (qrButton) {
    qrButton.on("click", () => {
      ["federation", "usernameField", "qrButton"].forEach(hideElement);
      ["federation-button", "usernameButton", "qrField"].forEach(showElement);
    });
  }

  if (usernameButton) {
    usernameButton.on("click", () => {
      ["federation", "qrField", "usernameButton"].forEach(hideElement);
      ["federation-button", "qrButton", "usernameField"].forEach(showElement);
    });
  }

  if (passkeyButton && WebAuthnJSON.supported()) {
    passkeyButton.removeClass("d-none");
  }

  if (deviceTrustSsoButton) {
    deviceTrustSsoButton.on("click", () => {
      initiateDeviceTrustLogin()
    })
  }
}

// Waits for the onBfpDataLoad event
function waitForBfpDataLoad() {
  return new Promise((resolve) => {
    $(document).one("onBfpDataLoad", () => resolve());
  });
}

// Waits for a valid form submission
function waitForFormSubmit() {
  return new Promise((resolve) => {
    const signinForm = $("#form-signin");
    const qrForm = $("#qr-form");

    const onSubmitCallback = (event) => {
      event.preventDefault();
      resolve(event);
    }
    signinForm.on("submit", onSubmitCallback);
    qrForm.on("submit", onSubmitCallback);
  });
}

async function handleLoginFormSubmit() {
  // Submits the form once when the required conditions are met
  const [submitEvent] = await Promise.all([
    waitForFormSubmit(),
    waitForBfpDataLoad(),
  ]);
  submitEvent.target.submit();
}

async function generateQrCode() {
  const loginQrCode = $("#login-qr-code");
  const loginQrCodeFailureMsg = $("#qr-code-failure-msg");

  try {
    const qrcodeSvgUrl = new URL(loginQrCode.data("qrpath"), window.location.origin);

    const pathArray = qrcodeSvgUrl.pathname.split("/");
    pathArray[4] = await computeHash(window.location.hostname);
    qrcodeSvgUrl.pathname = pathArray.join("/");

    const qrCodeSvgData = await $.get(qrcodeSvgUrl.toString());
    if (qrCodeSvgData && qrCodeSvgData.documentElement) {
      loginQrCode.append(qrCodeSvgData.documentElement);
      animateDisplayTransition("qr-code-loader", "qr-code-container");
    } else {
      loginQrCodeFailureMsg.show();
    }
  } catch (error) {
    console.error(error);
    loginQrCodeFailureMsg.show();
  }
}

function getDomainName()
{
  const hostname = window.location.hostname;
  return hostname.substring(hostname.lastIndexOf(".", hostname.lastIndexOf(".") - 1) + 1);
}

function showErrorMessage(message) {
  $("#error-message").text(message).removeClass("d-none");
}

async function loginWithPasskey() {
  if (!WebAuthnJSON.supported()) {
    return;
  }

  try {
    const form = $('#form-passkey');
    const challenge = form.find("input[name='challenge']").val();
    const publicKeyCredentialRequestOptions = {
      challenge,
      rpId: getDomainName(),
      userVerification: LoginPage.webauthnUserVerification,
    }
    const abortController = new AbortController()

    const credential = await WebAuthnJSON.get({
      publicKey: publicKeyCredentialRequestOptions,
      signal: abortController.signal
    })

    if (!credential) {
      showErrorMessage("No WebAuthn credential found. Please use another login method.")
      return
    }

    form.find("input[name='credential']").val(JSON.stringify(credential));
    form.find("input[name='challenge']").val(challenge);
    form.find("input[name='origin']").val(window.location.origin);
    form.find("input[name='remember_me']").val($("#remember_me").is(':checked'));
    form.submit();
  } catch (error) {
    console.error(error);
    showErrorMessage(`Authentication failed. Please try again or select another login method.`);
  }
}

async function initiateDeviceTrustLogin() {
  const deviceTrustTokenUrl = new URL($('#device-trust-sso-container').data("tokenpath"), window.location.origin);
  const tokenResponse = await $.get(deviceTrustTokenUrl.toString())

  subscribeToFayeChannel(tokenResponse.nonce, handleDeviceTrustFayeMessage)

  window.addEventListener('blur', () => clearTimeout(deviceTrustSsoTimeout))
  deviceTrustSsoTimeout = setTimeout(() => {
    handleDeviceTrustSsoTimeout()
  }, DEVICE_TRUST_SSO_TIMEOUT)
  window.location.href = `secureauth:authenticate:${tokenResponse.jwt}`
}

function handleDeviceTrustSsoTimeout() {
  clearTimeout(deviceTrustSsoTimeout)
  const timeoutError = document.getElementById("device-trust-sso-msgs").getAttribute("data-timeout");
  showErrorMessage(timeoutError)
}

function handleDeviceTrustFayeMessage(fayeResponse) {
  const jsonResponse = JSON.parse(fayeResponse);

  // Check for error response from backend
  if (jsonResponse.error_code) {
    handleDeviceTrustError(jsonResponse.error_message);
    return;
  }

  // Handle success case
  if (jsonResponse.user_id) {
    handleDeviceTrustResponse(jsonResponse.user_id);
  }
}

function handleDeviceTrustResponse(user_id) {
  clearTimeout(deviceTrustSsoTimeout);
  $('#device-trust-sso-user-id').val(user_id)
  const form = $('#device-trust-sso-callback-form')
  form.find("input[name='remember_me']").val($("#remember_me").is(':checked'));
  form.trigger('submit')
}

function handleDeviceTrustError(errorMessage) {
  clearTimeout(deviceTrustSsoTimeout);
  showErrorMessage(errorMessage || "Authentication failed.");
}

function initRememberMe() {
  const value = getCookieByName("remember-me");
  $("#remember_me").prop("checked", value === null || value === "true");
}

$(async () => {
  initRememberMe();
  subscribeToFayeChannel($("#channel").val(), () => $("#qr-form").submit());
  bindNavigationEvents();
  void generateQrCode();
  void handleLoginFormSubmit();
});

window.LoginPage = {
  loginWithPasskey,
  initiateDeviceTrustLogin,
  showErrorMessage,
  handleDeviceTrustFayeMessage,
  webauthnUserVerification: "preferred"
}
